import { Button, Form, Modal } from 'components';
import { MeterModelsSelect } from 'components/form/select/MeterModelsSelect';
import type { SerializedMeterModel } from '@enmon/commons/lib/tools';
import { useAssignModuleToModel } from 'api/hooks/meter-modules/useAssignModule';
import { useCallback, useState } from 'react';
import { Alert, Flex } from 'antd';
import { toArray } from '@enmon/commons';
import { useModuleDetailContext } from '../store';

export interface AssignModuleToModelModalProps {
  open: boolean;
  onClose: () => void;
}

export const AssignModuleToModelModal = ({ open, onClose }: AssignModuleToModelModalProps) => {
  const { module, moduleId } = useModuleDetailContext();
  const [form] = Form.useForm<{ modelId: SerializedMeterModel['id'] | null }>();
  const [selectedModel, setSelectedModel] = useState<SerializedMeterModel['id'] | null>(null);

  // Mutations
  const { mutate: assignModuleToModel, isLoading: isAssigning } = useAssignModuleToModel();

  // Handlers
  const handleCancel = useCallback(() => {
    form.resetFields();
    setSelectedModel(null);
    onClose();
  }, [form, onClose]);

  const handleAssign = useCallback(() => {
    if (!(module && moduleId && selectedModel)) return;

    assignModuleToModel(
      { module, modelId: selectedModel, assign: true },
      {
        onSuccess: () => {
          handleCancel();
        },
      },
    );
  }, [module, moduleId, selectedModel, assignModuleToModel, handleCancel]);

  return (
    <Modal footer={null} open={open} title="Assign to model" onCancel={handleCancel}>
      <Form form={form} layout="vertical">
        {!module?.technology && <Alert showIcon message="Make sure that module has technology saved." type="warning" />}

        <Form.Item label="Model" name="modelId">
          <MeterModelsSelect
            showSearch
            disabled={!module?.technology}
            filter={{ technologies: toArray(module?.technology) }}
            placeholder="Select model"
            onChange={value => setSelectedModel(value)}
          />
        </Form.Item>

        <Flex gap={8} justify="space-between">
          <Button onClick={handleCancel}>Cancel</Button>

          <Button disabled={!selectedModel} loading={isAssigning} type="primary" onClick={handleAssign}>
            Assign
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
